import React, { useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import Header from "@/components/Header";
import api, { formatApiError } from "@/lib/api";
import { CATEGORIES } from "@/pages/ClientCategoriesPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  UploadSimple,
  MapPin,
  X,
  Crosshair,
  ImageSquare,
  Sparkle,
} from "@phosphor-icons/react";

// Simple SVG grid "map" that lets the user click to drop a pin. Coordinates are
// normalized and mapped to a rough lat/lng centered at the current location.
const MapPicker = ({ pin, onPinChange }) => {
  const boxRef = useRef(null);

  const handleClick = (e) => {
    const rect = boxRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    // Convert to a synthetic lat/lng around 0,0 for demo purposes
    const lat = +(50 - (y / 100) * 100).toFixed(6);
    const lng = +((x / 100) * 360 - 180).toFixed(6);
    onPinChange({ x, y, lat, lng });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by your browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const x = ((longitude + 180) / 360) * 100;
        const y = ((50 - latitude) / 100) * 100;
        onPinChange({
          x: Math.min(100, Math.max(0, x)),
          y: Math.min(100, Math.max(0, y)),
          lat: +latitude.toFixed(6),
          lng: +longitude.toFixed(6),
        });
        toast.success("Location captured.");
      },
      () => toast.error("Could not get your location."),
      { enableHighAccuracy: true }
    );
  };

  return (
    <div className="space-y-3">
      <div
        ref={boxRef}
        onClick={handleClick}
        data-testid="location-map-picker"
        className="relative h-64 w-full cursor-crosshair overflow-hidden rounded-md border border-[#E5E2DB] bg-[#EDEAE2]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #D6D3CD 1px, transparent 1px), linear-gradient(to bottom, #D6D3CD 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      >
        {/* Decorative continents/blocks */}
        <div className="absolute left-[10%] top-[30%] h-16 w-24 rounded-sm bg-[#C6C1B7]/70" />
        <div className="absolute left-[40%] top-[20%] h-24 w-40 rounded-sm bg-[#C6C1B7]/70" />
        <div className="absolute left-[65%] top-[45%] h-20 w-28 rounded-sm bg-[#C6C1B7]/70" />
        <div className="absolute left-[25%] top-[65%] h-14 w-32 rounded-sm bg-[#C6C1B7]/70" />

        {pin && (
          <div
            className="absolute -translate-x-1/2 -translate-y-full"
            style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
            data-testid="location-map-pin"
          >
            <MapPin size={36} weight="fill" color="#C65D47" />
          </div>
        )}

        <p className="pointer-events-none absolute bottom-3 left-3 rounded bg-white/80 px-2 py-1 text-xs text-[#5C605E]">
          Click anywhere to drop a pin
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={useMyLocation}
          data-testid="use-my-location-button"
          className="border-[#0A0F0D] text-[#0A0F0D] hover:bg-[#0A0F0D] hover:text-[#FFFFFF]"
        >
          <Crosshair size={16} weight="bold" className="mr-2" />
          Use my current location
        </Button>
        {pin && (
          <p className="text-xs text-[#5C605E]" data-testid="location-map-coords">
            Pinned at <strong>{pin.lat}°</strong>, <strong>{pin.lng}°</strong>
          </p>
        )}
      </div>
    </div>
  );
};

const JobPostingPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initialCategory = params.get("category") || "";

  const [category, setCategory] = useState(
    CATEGORIES.some((c) => c.id === initialCategory) ? initialCategory : ""
  );
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState("");
  const [address, setAddress] = useState("");
  const [pin, setPin] = useState(null);

  const [photos, setPhotos] = useState([]); // { id, previewUrl, uploading, remoteId }
  const [submitting, setSubmitting] = useState(false);
  const [aiExpanding, setAiExpanding] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const fileInputRef = useRef(null);

  const handleAiExpand = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Type a short prompt first (e.g. 'kitchen socket sparking').");
      return;
    }
    if (!category) {
      toast.error("Pick a category first so the AI knows the context.");
      return;
    }
    setAiExpanding(true);
    try {
      const { data } = await api.post("/ai/expand-job", {
        prompt: aiPrompt,
        category,
      });
      setTitle(data.title);
      setDescription(data.description);
      toast.success("AI expanded your prompt into a full description");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAiExpanding(false);
    }
  };

  const activeCategory = useMemo(
    () => CATEGORIES.find((c) => c.id === category),
    [category]
  );

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;

    for (const file of files) {
      const localId = Math.random().toString(36).slice(2);
      const previewUrl = URL.createObjectURL(file);
      setPhotos((prev) => [...prev, { id: localId, previewUrl, uploading: true, remoteId: null }]);

      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await api.post("/uploads/photo", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((prev) =>
          prev.map((p) =>
            p.id === localId ? { ...p, uploading: false, remoteId: data.id } : p
          )
        );
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
        setPhotos((prev) => prev.filter((p) => p.id !== localId));
      }
    }
  };

  const removePhoto = (localId) => {
    setPhotos((prev) => prev.filter((p) => p.id !== localId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) return toast.error("Please pick a category.");
    if (photos.some((p) => p.uploading)) return toast.error("Photos are still uploading…");

    setSubmitting(true);
    try {
      const { data } = await api.post("/jobs", {
        category,
        title,
        description,
        budget,
        location: {
          address,
          lat: pin?.lat ?? null,
          lng: pin?.lng ?? null,
        },
        photo_ids: photos.map((p) => p.remoteId).filter(Boolean),
      });
      toast.success("Job posted!");
      navigate(`/client/dashboard`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="job-posting-page">
      <Toaster position="top-right" richColors />
      <Header />
      <main className="mx-auto max-w-3xl px-6 py-16 md:px-10 md:py-20">
        <div className="animate-fade-up">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#C65D47]">
            Step 03 / 03 — Post your job
          </p>
          <h1 className="mt-3 font-display text-4xl font-bold tracking-tight text-[#0A0F0D] sm:text-5xl">
            Describe the work.
          </h1>
          <p className="mt-4 text-base leading-relaxed text-[#5C605E]">
            Add photos and pin your location — local {activeCategory?.label?.toLowerCase() || "pros"} will reach out with a quote.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-12 space-y-8 rounded-md border border-[#E5E2DB] bg-white p-6 md:p-10"
          data-testid="job-posting-form"
        >
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-[#0A0F0D]">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger
                  className="border-[#E5E2DB] bg-white"
                  data-testid="job-category-select"
                >
                  <SelectValue placeholder="Choose a category…" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem
                      key={c.id}
                      value={c.id}
                      data-testid={`job-category-option-${c.id}`}
                    >
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="budget" className="text-[#0A0F0D]">Budget (optional)</Label>
              <Input
                id="budget"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder="e.g. R200-500"
                data-testid="job-budget-input"
                className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="job-title" className="text-[#0A0F0D]">Job title</Label>
            <Input
              id="job-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder="Replace kitchen socket"
              data-testid="job-title-input"
              className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="job-desc" className="text-[#0A0F0D]">Describe the work</Label>
            {/* AI describer — expand a short prompt into a full title + description */}
            <div
              className="mb-3 rounded-md border border-dashed border-[#C65D47]/50 bg-[#FFF6F1] p-3"
              data-testid="ai-expand-block"
            >
              <p className="text-xs font-semibold uppercase tracking-widest text-[#C65D47]">
                <Sparkle size={12} weight="fill" className="mr-1 inline align-[-1px]" />
                AI describe my job
              </p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <Input
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="e.g. kitchen socket sparking when I plug things in"
                  data-testid="ai-prompt-input"
                  className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
                />
                <Button
                  type="button"
                  onClick={handleAiExpand}
                  disabled={aiExpanding}
                  data-testid="ai-expand-button"
                  className="bg-[#C65D47] text-white hover:bg-[#0A0F0D]"
                >
                  {aiExpanding ? "Writing…" : "Expand"}
                </Button>
              </div>
            </div>
            <Textarea
              id="job-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={5}
              placeholder="What needs doing, when you'd like it done, any details a pro should know…"
              data-testid="job-description-input"
              className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
            />
          </div>

          {/* Photo upload */}
          <div className="space-y-3">
            <Label className="text-[#0A0F0D]">Photos of the job</Label>
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFiles(e.dataTransfer.files);
              }}
              data-testid="photo-dropzone"
              className="flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-[#D6D3CD] bg-[#FFFFFF] px-6 py-10 text-center transition-colors hover:border-[#C65D47]"
            >
              <UploadSimple size={28} weight="duotone" color="#0A0F0D" />
              <p className="mt-3 font-display text-lg font-semibold text-[#0A0F0D]">
                Drop photos here or click to browse
              </p>
              <p className="mt-1 text-xs text-[#5C605E]">JPG, PNG or WEBP. Multiple files supported.</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="photo-file-input"
              />
            </div>

            {photos.length > 0 && (
              <div
                className="grid grid-cols-3 gap-3 md:grid-cols-4"
                data-testid="photo-preview-grid"
              >
                {photos.map((p) => (
                  <div
                    key={p.id}
                    className="group relative aspect-square overflow-hidden rounded-md border border-[#E5E2DB] bg-[#FFFFFF]"
                    data-testid={`photo-preview-${p.id}`}
                  >
                    <img src={p.previewUrl} alt="preview" className="h-full w-full object-cover" />
                    {p.uploading ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-[#0A0F0D]/50 text-xs text-white">
                        <ImageSquare size={18} weight="duotone" className="mr-1 animate-pulse" />
                        Uploading…
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => removePhoto(p.id)}
                        data-testid={`photo-remove-${p.id}`}
                        className="absolute right-1 top-1 rounded-full bg-[#0A0F0D] p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <X size={14} weight="bold" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Location */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="address" className="text-[#0A0F0D]">Address</Label>
              <Input
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St, City"
                data-testid="job-address-input"
                className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
              />
            </div>
            <div>
              <Label className="mb-2 block text-[#0A0F0D]">Pin your location</Label>
              <MapPicker pin={pin} onPinChange={setPin} />
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-[#E5E2DB] pt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate("/client/categories")}
              data-testid="job-back-button"
              className="text-[#5C605E] hover:text-[#0A0F0D]"
            >
              Back
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="job-submit-button"
              className="bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
            >
              {submitting ? "Posting…" : "Post job"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default JobPostingPage;
